import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { Form, Field, Formik } from "lib/formik"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Select from "components/Shared/TranslatedSelect"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Modal,
  ModalBody,
  Row,
  UncontrolledTooltip,
} from "reactstrap"
import {
  addWalletCoupon,
  getWalletCouponDetails,
  updateWalletCoupon,
} from "store/actions/promotionsAction"
import { getAllCustomersList } from "store/actions/customersAction"
import "flatpickr/dist/themes/material_blue.css"
import { showAlert } from "store/reducers/alertReducer"
import { addCouponRedeemSchema } from "components/Common/validation"
import Spinners from "components/Shared/Spinners"
import { DatePicker } from "rsuite"
import isBefore from "date-fns/isBefore"
import addWalletCouponTemplate from "assets/file/addWalletCouponTemplate.csv"
import { startOfDay } from "date-fns"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import useCrossModuleFilterPermissions from "hooks/useCrossModuleFilterPermissions"

const initialValues = {
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
  code: "",
  walletAmount: "",
  sourceType: "open",
  startsAt: "",
  expiresAt: "",
  file: "",
  maxCount: "",
  userIds: [],
}

const AddCouponRedeem = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : ""} 
  ${mode === "edit" ? t("Edit") : ""} 
   ${t("Wallet Coupon ")}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const [formValues, setFormValues] = useState(initialValues)
  const [showFailedModal, setShowFailedModal] = useState(false)
  const [failedCustomersCsv, setFailedCustomersCsv] = useState("")

  const { couponDetails, loading, isLoading } = useSelector(
    state => state.Promotions
  )
  const { customersDropdownList } = useSelector(state => state.Customers)
  const { userRole, accountType } = useSelector(state => state.Login)
  const { canFilterByCustomers } = useCrossModuleFilterPermissions()

  const [customerOptions, setCustomerOptions] = useState([])

  const fetchCustomersList = keyword => {
    if (!canFilterByCustomers) return
    dispatch(getAllCustomersList({ keyword }))
  }

  useEffect(() => {
    if (customersDropdownList && customersDropdownList.length > 0) {
      const updatedCustomerOption = customersDropdownList.map(item => {
        const label =
          `${item.firstName || ""} ${item.lastName || ""}${
            item.phoneNumber ? `, ${item.phoneNumber}` : ""
          }`.trim() ||
          item.name ||
          item.phoneNumber ||
          item._id
        return { label, value: item._id }
      })
      setCustomerOptions(updatedCustomerOption)
    }
  }, [customersDropdownList])

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      fetchCouponDetails()
    }
  }, [])

  useEffect(() => {
    if (couponDetails && !loading && mode === "edit") {
      const {
        name,
        nameAr,
        description,
        descriptionAr,
        code,
        startsAt,
        expiresAt,
        walletAmount,
        sourceType,
        maxCount,
        assignedCustomers,
      } = couponDetails

      const updatedUserIds = (assignedCustomers || [])
        .filter(ac => ac.userId && !ac.isDeleted)
        .map(ac => {
          const u = ac.userId
          const id = String((u && u._id) || u)
          const namePart = [u && u.firstName, u && u.name, u && u.lastName]
            .filter(Boolean)
            .join(" ")
            .trim()
          const phoneSuffix = u && u.phoneNumber ? `, ${u.phoneNumber}` : ""
          const label = namePart ? namePart + phoneSuffix : id
          return { label, value: id }
        })

      setFormValues(
        Object.assign({}, formValues, {
          name,
          nameAr,
          code,
          description: description || "",
          descriptionAr: descriptionAr || "",
          walletAmount: walletAmount != null ? walletAmount : "",
          sourceType: sourceType || "open",
          startsAt: new Date(startsAt),
          expiresAt: new Date(expiresAt),
          maxCount: maxCount != null ? maxCount : "",
          userIds: updatedUserIds,
        })
      )
    }
  }, [couponDetails])

  const fetchCouponDetails = () => {
    const payload = { id }
    dispatch(getWalletCouponDetails(payload))
  }

  const handleOnSubmit = values => {
    const {
      name,
      nameAr,
      description,
      descriptionAr,
      code,
      walletAmount,
      sourceType,
      startsAt,
      expiresAt,
      file,
      maxCount,
      userIds,
    } = values

    let data = new FormData()
    data.append("name", name)
    data.append("nameAr", nameAr)
    data.append("description", description)
    data.append("descriptionAr", descriptionAr)
    data.append("code", code)
    data.append("walletAmount", walletAmount)
    data.append("startsAt", startsAt)
    data.append("expiresAt", expiresAt)
    data.append(
      "sourceType",
      file || (userIds && userIds.length > 0)
        ? "assigned"
        : sourceType || "open"
    )
    if (maxCount !== "" && maxCount != null) {
      data.append("maxCount", maxCount)
    }
    if (userIds && Array.isArray(userIds)) {
      const ids = userIds.map(u => {
        if (typeof u === "object" && u && u.value) return u.value
        return u
      })
      data.append("userIds", JSON.stringify(ids))
    }
    if (mode === "add") {
      data.append("file", file)
      dispatch(addWalletCoupon(data))
        .then(action => {
          const payload = action ? action.payload : {}
          const {
            statusCode,
            status,
            msg,
            failedCustomersCount,
            failedCustomersCsv: csvData,
          } = payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            if (failedCustomersCount > 0 && csvData) {
              setFailedCustomersCsv(csvData)
              setShowFailedModal(true)
            } else {
              navigate(`/wallet-coupons`)
            }
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
    if (mode === "edit") {
      data.append("id", id)
      if (file) data.append("file", file)
      dispatch(updateWalletCoupon(data))
        .then(action => {
          const payload = action ? action.payload : {}
          const {
            statusCode,
            status,
            msg,
            failedCustomersCount,
            failedCustomersCsv: csvData,
          } = payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            if (failedCustomersCount > 0 && csvData) {
              setFailedCustomersCsv(csvData)
              setShowFailedModal(true)
            } else {
              navigate(`/wallet-coupons`)
            }
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
            if (failedCustomersCount > 0 && csvData) {
              setFailedCustomersCsv(csvData)
              setShowFailedModal(true)
            }
          }
        })
        .catch(error => {
          const errPayload =
            (error && error.response && error.response.data) || error
          const { failedCustomersCount, failedCustomersCsv: csvData } =
            errPayload || {}
          if (failedCustomersCount > 0 && csvData) {
            setFailedCustomersCsv(csvData)
            setShowFailedModal(true)
          }
        })
    }
  }

  const generateCoupon = setFieldValue => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    let code = ""
    const length = 10

    for (let i = 0; i < length; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    setFieldValue("code", code)
  }

  const handleDownloadFailedList = () => {
    if (!failedCustomersCsv) return
    const blob = new Blob([failedCustomersCsv], {
      type: "text/csv;charset=utf-8;",
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "failed-customers.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleCloseFailedModal = () => {
    setShowFailedModal(false)
    setFailedCustomersCsv("")
    navigate(`/wallet-coupons`)
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid add_coupon_section">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" && t("Add")}
                  {mode === "edit" && t("Edit")} {t("Wallet Coupons")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addCouponRedeemSchema(
                    mode,
                    mode === "edit" ? couponDetails?.maxCount : undefined
                  )}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              {t("Description(English)")}
                            </label>
                            <Field
                              as="textarea"
                              id="description"
                              name="description"
                              placeholder={t("Description(English)")}
                              className={`form-control`}
                            />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              {t("Description(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              id="descriptionAr"
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              style={{ direction: "rtl" }}
                              className={`form-control ${
                                touched.descriptionAr && errors.descriptionAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="descriptionAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="code">
                              <span className="text-danger">*</span>{" "}
                              {t("Coupon Code")}
                            </label>
                            <div className="position-relative coupon-code-field">
                              <Field
                                type="text"
                                id="code"
                                name="code"
                                placeholder={t("Coupon Code")}
                                disabled={mode === "edit"}
                                className={`form-control ${
                                  touched.code && errors.code
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              {mode !== "edit" && (
                                <>
                                  <i
                                    className="bx bx-sync bg-primary g_center coupon-code-generate"
                                    onClick={() =>
                                      generateCoupon(setFieldValue)
                                    }
                                    id="couponRedeem"
                                  ></i>
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`couponRedeem`}
                                  >
                                    {t("Generate Code")}
                                  </UncontrolledTooltip>
                                </>
                              )}
                            </div>
                            <TextError name="code" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="walletAmount">
                              <span className="text-danger">*</span>{" "}
                              {t("Wallet Amount")}
                            </label>
                            <Field
                              type="number"
                              id="walletAmount"
                              name="walletAmount"
                              placeholder={t("Wallet Amount")}
                              min="0"
                              step="1"
                              onKeyDown={e => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                  e.preventDefault()
                                }
                              }}
                              className={`form-control ${
                                touched.walletAmount && errors.walletAmount
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="walletAmount" />
                          </div>
                        </div>
                        {mode !== "edit" && (
                          <>
                            <div className="col-lg-3">
                              <div className="form_field">
                                <label htmlFor="file">{t("Customers list")}</label>
                                <TranslatedFileInput
                                  id="file"
                                  name="file"
                                  accept=".csv"
                                  onChange={e => {
                                    setFieldValue("file", e.currentTarget.files[0])
                                  }}
                                />
                                <TextError name="file" />
                              </div>
                            </div>
                            <div className="col-lg-3">
                              <div style={{ marginTop: 36 }}>
                                <Button
                                  color="primary"
                                  className="ms-3"
                                  type="button"
                                >
                                  <a
                                    href={addWalletCouponTemplate}
                                    download="CSV template.csv"
                                    className="text-white"
                                  >
                                    {t("Download CSV template")}
                                  </a>
                                </Button>
                              </div>
                            </div>

                            <div className="col-lg-6">
                              <div className="form_field">
                                <label htmlFor="userIds">
                                  {t("Search & Select Users")}
                                </label>
                                <Select
                                  value={values.userIds}
                                  isMulti
                                  onChange={selectedValue => {
                                    setFieldValue("userIds", selectedValue || [])
                                  }}
                                  options={customerOptions}
                                  onInputChange={value => fetchCustomersList(value)}
                                  placeholder={t("Please Type To Search Customer")}
                                  className="select2-selection"
                                  classNamePrefix="select2"
                                  isSearchable
                                />
                                <small className="text-muted d-block mt-1">
                                  {t(
                                    "Search users by name or phone to assign this coupon"
                                  )}
                                </small>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="maxCount">
                              {t("Max Redemptions")}
                            </label>
                            <Field
                              type="number"
                              id="maxCount"
                              name="maxCount"
                              placeholder={t("Max Redemptions")}
                              min={
                                mode === "edit" && couponDetails?.maxCount != null
                                  ? couponDetails.maxCount
                                  : "1"
                              }
                              step="1"
                              onKeyDown={e => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                  e.preventDefault()
                                }
                              }}
                              className="form-control"
                            />
                            <small className="text-muted d-block mt-1">
                              {t(
                                "Required when neither CSV nor users are selected. Limits total redemptions."
                              )}
                            </small>
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="endTime">
                              <span className="text-danger">*</span>{" "}
                              {t("Start Date & Time")}
                            </label>
                            <DatePicker
                              showTime={{ format: "HH:mm" }}
                              format="yyyy-MM-dd HH:mm"
                              onChange={value => {
                                setFieldValue("startsAt", value)
                              }}
                              value={values.startsAt}
                              shouldDisableDate={date =>
                                isBefore(
                                  startOfDay(date),
                                  startOfDay(new Date())
                                )
                              }
                              placement="autoVerticalStart"
                              placeholder=" 2024-07-16 14:30"
                            />

                            <TextError name="startsAt" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="endTime">
                              <span className="text-danger">*</span>{" "}
                              {t("End Date & Time")}
                            </label>
                            <DatePicker
                              showTime={{ format: "HH:mm" }}
                              format="yyyy-MM-dd HH:mm"
                              onChange={value => {
                                setFieldValue("expiresAt", value)
                              }}
                              value={values.expiresAt}
                              shouldDisableDate={date =>
                                isBefore(
                                  startOfDay(date),
                                  startOfDay(new Date())
                                )
                              }
                              placement="autoVerticalStart"
                              placeholder="2024-07-20 14:30"
                            />

                            <TextError name="expiresAt" />
                          </div>
                        </div>

                        <div className="mt-2"></div>
                        <div className="col-12 d-flex justify-content-end mt-5 mb-5">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        isOpen={showFailedModal}
        toggle={handleCloseFailedModal}
        centered
        size="md"
      >
        <div className="modal-content">
          <ModalBody className="px-4 py-5 text-center">
            <button
              type="button"
              onClick={handleCloseFailedModal}
              className="btn-close position-absolute end-0 top-0 m-3"
              aria-label="Close"
            />
            <div className="avatar-sm mb-4 mx-auto">
              <div className="avatar-title bg-warning text-warning bg-opacity-10 font-size-20 rounded-3">
                <i className="bx bx-error-circle"></i>
              </div>
            </div>
            <p className="text-muted font-size-16 mb-4">
              {t("For these users we failed to send the coupon")}
            </p>
            <div className="hstack gap-2 justify-content-center mb-0">
              <Button
                type="button"
                color="secondary"
                onClick={handleCloseFailedModal}
              >
                {t("Close")}
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={handleDownloadFailedList}
              >
                {t("Download Failed List")}
              </Button>
            </div>
          </ModalBody>
        </div>
      </Modal>
    </div>
  )
}

export default AddCouponRedeem
